import { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Check, Users, MessageCircle, PenLine, Facebook, Youtube, Instagram } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, Eyebrow } from "@/components/Reveal";
import { countries } from "@/data/countries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topics = [
  { value: "question", label: "I have a question" },
  { value: "networking", label: "Connect me with moms in a city" },
  { value: "correction", label: "Something on the site is outdated" },
  { value: "collab", label: "Collaboration / press" },
  { value: "other", label: "Something else" },
];

const ways = [
  { icon: MessageCircle, title: "Ask anything", text: "Visa confusion, school fees, whether to sell the house. If I don't know, I'll find a sister who does." },
  { icon: Users, title: "Find your people", text: "Tell me where you're headed and I'll try to connect you with mothers already living there." },
  { icon: PenLine, title: "Share your city", text: <>Lived somewhere on the map? Open that country's profile and use the <strong className="font-semibold text-pine">Mom/Sister Reviews</strong> tab — every submission is reviewed before publishing.</>, link: { to: "/countries", label: "Browse country profiles" } },
];

export const socialLinks = [
  { name: "WhatsApp group", handle: "Join the sisters' chat", url: "https://chat.whatsapp.com/Gt0Bg2Ui8FkCC0xokywWVE", icon: MessageCircle, cls: "bg-[#25D366] text-white", testid: "social-whatsapp" },
  { name: "Facebook", handle: "Follow Hijrah Mom", url: "https://www.facebook.com/profile.php?id=61594450504673", icon: Facebook, cls: "bg-[#1877F2] text-white", testid: "social-facebook" },
  { name: "YouTube", handle: "Watch the journey", url: "https://www.youtube.com/channel/UCffQM0OJ0nk2OqBSijW7bJQ", icon: Youtube, cls: "bg-[#FF0000] text-white", testid: "social-youtube" },
  { name: "Instagram", handle: "@HijrahMom83", url: "https://www.instagram.com/HijrahMom83", icon: Instagram, cls: "bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white", testid: "social-instagram" },
];

const initial = { name: "", email: "", topic: "question", location: "", message: "", connect_ok: false };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      const res = await fetch(`${API}/contact`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!res.ok) throw new Error("failed");
      setState("done");
    } catch {
      setState("error");
    }
  };

  const input = "w-full bg-white border border-pine/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-petal/40";

  return (
    <div data-testid="contact-page" className="pt-24 lg:pt-28 bg-cream grain relative min-h-screen">
      <Seo
        title="Connect with Hijrah Mom — Questions, Networking & Sisterhood"
        siteName="Hijrah Mom"
        description="Ask a question, get connected with American Muslim mothers already living abroad, or share a correction. Every message is read by Hijrah Mom herself."
        image="/og-image.jpg"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Eyebrow>Connect &amp; network</Eyebrow>
          <h1 className="mt-4 font-serif font-bold text-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            No mother should <span className="italic text-petal">make hijrah alone.</span>
          </h1>
          <p className="mt-6 text-sage leading-relaxed">
            Send me a note. I read every message myself, usually between school runs, and I'll do my best to connect you with sisters already on the ground.
          </p>
          <div className="mt-8 flex flex-col gap-3 max-w-md" data-testid="social-links">
            {socialLinks.map((l) => (
              <a
                key={l.name}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={l.testid}
                className="group bg-white border border-pine/15 rounded-2xl p-4 flex items-center gap-3 hover:border-petal hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
              >
                <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${l.cls}`}><l.icon size={18} /></span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-ink">{l.name}</span>
                  <span className="block text-xs text-sage">{l.handle}</span>
                </span>
                <span className="text-petal text-sm">→</span>
              </a>
            ))}
          </div>
          <div className="mt-10 space-y-6">
            {ways.map((w, i) => (
              <FadeUp key={w.title} delay={i * 0.08} className="flex gap-4">
                <span className="w-11 h-11 rounded-full bg-blush-deep text-petal flex items-center justify-center shrink-0"><w.icon size={18} /></span>
                <div>
                  <h3 className="font-serif font-semibold text-ink text-lg">{w.title}</h3>
                  <p className="mt-1 text-sm text-sage leading-relaxed">{w.text}</p>
                  {w.link && (
                    <Link to={w.link.to} data-testid="contact-reviews-link" className="inline-block mt-2 text-sm font-semibold text-petal hover:text-pine transition-colors underline underline-offset-4">
                      {w.link.label} →
                    </Link>
                  )}
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        <FadeUp className="lg:col-span-7" delay={0.1}>
          <div className="bg-white rounded-3xl border border-pine/10 shadow-sm p-6 lg:p-10">
            {state === "done" ? (
              <div data-testid="contact-success" className="text-center py-12">
                <span className="inline-flex w-14 h-14 rounded-full bg-pine text-cream items-center justify-center"><Check size={24} /></span>
                <h2 className="mt-6 font-serif font-semibold text-ink text-2xl">JazakAllahu khair — your note landed.</h2>
                <p className="mt-3 text-sm text-sage max-w-md mx-auto">I'll reply to <span className="font-semibold text-ink">{form.email}</span> as soon as I can, insha'Allah. Check your spam folder if it's been a week.</p>
                <button data-testid="contact-again-btn" onClick={() => { setForm(initial); setState("idle"); }} className="mt-8 text-sm font-semibold text-petal hover:text-pine transition-colors">Send another message</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-pine">Your name</span>
                    <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={`mt-2 ${input}`} placeholder="Umm Yusuf" />
                  </label>
                  <label className="block">
                    <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-pine">Email</span>
                    <input data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={`mt-2 ${input}`} placeholder="you@example.com" />
                  </label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-pine">What's this about?</span>
                    <select data-testid="contact-topic-select" value={form.topic} onChange={set("topic")} className={`mt-2 ${input}`}>
                      {topics.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-pine">City / country you're eyeing</span>
                    <input data-testid="contact-location-input" list="country-options" value={form.location} onChange={set("location")} className={`mt-2 ${input}`} placeholder="Kuala Lumpur, Malaysia" />
                    <datalist id="country-options">
                      {countries.map((c) => <option key={c.slug} value={c.name} />)}
                    </datalist>
                  </label>
                </div>
                <label className="block">
                  <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-pine">Your message</span>
                  <textarea data-testid="contact-message-input" required minLength={10} rows={6} value={form.message} onChange={set("message")} className={`mt-2 ${input} resize-y`} placeholder="Assalamu alaikum! We're a family of five looking at..." />
                </label>
                <label className="flex items-start gap-3 text-sm text-sage cursor-pointer">
                  <input data-testid="contact-connect-checkbox" type="checkbox" checked={form.connect_ok} onChange={set("connect_ok")} className="mt-1 accent-[#D9777F]" />
                  <span>You may share my first name and email with other Hijrah Mom sisters heading to the same city, so we can connect.</span>
                </label>
                {state === "error" && <p data-testid="contact-error" className="text-sm text-petal-dark font-medium">Something went wrong, sis — please try again.</p>}
                <button data-testid="contact-submit-btn" type="submit" disabled={state === "loading"} className="inline-flex items-center gap-2 bg-petal text-white text-sm font-semibold px-7 py-3.5 rounded-full shadow-md shadow-petal/25 hover:bg-pine transition-colors disabled:opacity-60">
                  <Send size={15} /> {state === "loading" ? "Sending..." : "Send my note"}
                </button>
              </form>
            )}
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
